import { ref, computed, watch } from 'vue'
import { authState, waitUntilReady } from "@/ui-plus/useAuth";
import { type ResType } from '@api-client/typeUtils';
import { dashboardApiClient } from '@api-client/dashboard';
import { zmgPublicCacheClient } from '@api-client/zmg_cache';
import { loadCurrentWebsiteStripeStatuses } from './CurrentWebsiteStripeStatusStore';

export type websiteListType = ResType<
    typeof dashboardApiClient.website.my_websites.$get
>["data"];

type websiteType = websiteListType[number] & {
    isSupportTeamPartner: boolean;
    isMarketingTeamPartner: boolean;
};

export const myWebsites = ref<websiteType[]>([]);
export const searchQuery = ref('')
export const myWebsitesFiltered = computed(() =>
    myWebsites.value.filter(website =>
        website.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        website.defaultHostname?.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
        // isSupportTeamPartner or isMarketingTeamPartner first
        .sort((a, b) => {
            if (a.isSupportTeamPartner && !b.isSupportTeamPartner) return -1;
            if (!a.isSupportTeamPartner && b.isSupportTeamPartner) return 1;
            if (a.isMarketingTeamPartner && !b.isMarketingTeamPartner) return -1;
            if (!a.isMarketingTeamPartner && b.isMarketingTeamPartner) return 1;
            return 0;
        })
)

export const currentWebsiteId = ref<string | null>(null);

export const currentWebsite = computed(() => {
    if (!currentWebsiteId.value) return null;
    return myWebsites.value.find(website => website.id === currentWebsiteId.value);
});


export async function loadMyWebsites() {
    console.log('loadMyWebsites');
    const { data, error } = await (await dashboardApiClient.website.my_websites.$get()).json()
    if (error) {
        console.error(error)
        return
    } else {
        myWebsites.value = data.map(website => ({
            ...website,
            isSupportTeamPartner: false,
            isMarketingTeamPartner: false,
        }));
        // my_websites has no partner flags — restore them for the open website
        if (currentWebsiteId.value) {
            await loadIsPartnerInfo(currentWebsiteId.value);
        }
    }
}

export async function loadIsPartnerInfo(wbId: string) {
    const website = myWebsites.value.find(website => website.id === wbId);
    if (website) {
        website.isSupportTeamPartner = false;
        website.isMarketingTeamPartner = false;
    }
    const { data, error } = await (await zmgPublicCacheClient.website.partner_info[':id']['data.json'].$get({
        param: { id: wbId },
    })).json();
    if (error) {
        console.error(error);
    } else if (data && website) {
        website.isSupportTeamPartner = data.isSupportTeamPartner;
        website.isMarketingTeamPartner = data.isMarketingTeamPartner;
    }
}

export function setCurrentWebsiteId(websiteId: string) {
    currentWebsiteId.value = websiteId
    loadCurrentWebsiteStripeStatuses();
    setTimeout(() => {
        loadIsPartnerInfo(websiteId);
    }, 500);
}

export function setCurrentWebsiteName(name: string) {
    if (!currentWebsiteId.value) return;
    const website = myWebsites.value.find(website => website.id === currentWebsiteId.value);
    if (website) {
        website.name = name;
    }
}

waitUntilReady.then(() => {
    if (authState.value.accessTokenPayload !== null) {
        loadMyWebsites();
    } else {
        const stop = watch(authState, (s) => {
            if (s?.accessTokenPayload !== null) {
                loadMyWebsites();
                stop();
            }
        });
    }
});
